import { useState } from "react";
import supabase from "./../Config/Supabase";
import { Link } from "react-router-dom";
import logo from "../Assets/HerVeg.png";
import { motion, AnimatePresence } from "framer-motion";
import { Eye, EyeOff, Mail, Lock, AlertCircle, ArrowRight } from "lucide-react";
import landscape from "../Assets/landscapes.png";
import { Loader } from "lucide-react";

const inputVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: { delay: i * 0.12, duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function UserLogin() {
  const [present, setPresent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [focused, setFocused] = useState(null);
  const [error, setError] = useState("");

  const [Login, setData] = useState({ email: "", password: "" });

  const loging = async () => {
    setLoading(true);
    const { data, error } = await supabase.auth.signInWithPassword({
      email: Login.email,
      password: Login.password,
    });

    if (error) {
      setError(error.message);
      setLoading(false);
      setPresent(true);
    } else {
      const { session } = data;
      localStorage.setItem("token", session.access_token);
      window.location = "/home/home";
      setPresent(false);
      setData({ email: "", password: "" });
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
    if (present) setPresent(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (Login.email.length !== 0 && Login.password.length !== 0) {
      setPresent(false);
      loging();
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#f5f7f2] dark:bg-[#0e1410]">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 pointer-events-none">
        <svg
          className="absolute top-0 left-0 w-full h-full opacity-[0.03] dark:opacity-[0.06]"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <pattern
              id="grid"
              width="48"
              height="48"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 48 0 L 0 0 0 48"
                fill="none"
                stroke="#1a4a2e"
                strokeWidth="1"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>

        {/* Decorative blobs */}
        <motion.div
          animate={{ scale: [1, 1.05, 1], rotate: [0, 5, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-green-200/30 dark:bg-green-900/20 blur-3xl"
        />
        <motion.div
          animate={{ scale: [1, 1.08, 1], rotate: [0, -5, 0] }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2,
          }}
          className="absolute -bottom-40 -left-24 w-[400px] h-[400px] rounded-full bg-emerald-200/25 dark:bg-emerald-900/15 blur-3xl"
        />
      </div>
      {/* landscape image */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 left-0 right-0">
          <img
            src={landscape}
            alt="Herveg05 farm illustration"
            className="object-cover opacity-85"
          />
        </div>
      </div>

      {/* Card */}
      <motion.div
        initial={{ opacity: 0, y: 40, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-md mx-4"
      >
        <div className="overflow-hidden backdrop-blur-xl">
          <div className="px-2 pt-10 pb-10 md:px-6">
            {/* Logo & heading */}
            <motion.div
              custom={0}
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              className="mb-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="flex items-center ">
                  <img
                    src={logo}
                    alt="HerVeg logo"
                    className="object-contain "
                  />
                </div>
              </div>

              <h1 className="text-xl font-bold leading-tight tracking-tight text-gray-900 md:text-xl dark:text-white">
                Welcome back
              </h1>
              <p className="mt-1.5 text-xs md:text-sm text-gray-500 dark:text-gray-400">
                Sign in to your account to continue
              </p>
            </motion.div>
            {/* Error alert */}
            <AnimatePresence>
              {present && (
                <motion.div
                  initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                  animate={{ opacity: 1, height: "auto", marginBottom: 20 }}
                  exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                >
                  <div className="flex items-start gap-3 p-4 border border-red-200 rounded-xl bg-red-50 dark:bg-red-950/40 dark:border-red-800/60">
                    <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                    <p className="text-sm leading-snug text-red-700 dark:text-red-400">
                      {error}
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email field */}
              <motion.div
                custom={1}
                variants={fadeUp}
                initial="hidden"
                animate="visible"
              >
                <label
                  htmlFor="email"
                  className="block mb-2 text-xs font-semibold tracking-wider text-gray-500 uppercase dark:text-gray-400"
                >
                  Email address
                </label>
                <div
                  className={`relative flex items-center rounded-xl border transition-all duration-200 ${
                    focused === "email"
                      ? "border-green-500 shadow-[0_0_0_3px_rgba(34,197,94,0.12)]"
                      : "border-gray-300 dark:border-white/10"
                  } bg-gray-50/70 dark:bg-white/[0.04]`}
                >
                  <Mail
                    className={`absolute left-3.5 w-4 h-4 transition-colors duration-200 ${
                      focused === "email" ? "text-green-500" : "text-gray-400"
                    }`}
                  />
                  <motion.input
                    variants={inputVariants}
                    custom={0}
                    initial="hidden"
                    animate="visible"
                    id="email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    value={Login.email}
                    onChange={handleChange}
                    onFocus={() => setFocused("email")}
                    onBlur={() => setFocused(null)}
                    placeholder="you@example.com"
                    className="w-full py-3 pl-10 pr-4 text-sm text-gray-900 bg-transparent outline-none dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-600"
                  />
                </div>
              </motion.div>

              {/* Password field */}
              <motion.div
                custom={2}
                variants={fadeUp}
                initial="hidden"
                animate="visible"
              >
                <div className="flex items-center justify-between mb-2">
                  <label
                    htmlFor="password"
                    className="block text-xs font-semibold tracking-wider text-gray-500 uppercase dark:text-gray-400"
                  >
                    Password
                  </label>
                  <Link
                    to="/passwordreset"
                    className="text-xs font-medium text-green-600 transition-colors dark:text-green-400 hover:text-green-700 dark:hover:text-green-300"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div
                  className={`relative flex items-center rounded-xl border transition-all duration-200 ${
                    focused === "password"
                      ? "border-green-500 shadow-[0_0_0_3px_rgba(34,197,94,0.12)]"
                      : "border-gray-300 dark:border-white/10"
                  } bg-gray-50/70 dark:bg-white/[0.04]`}
                >
                  <Lock
                    className={`absolute left-3.5 w-4 h-4 transition-colors duration-200 ${
                      focused === "password"
                        ? "text-green-500"
                        : "text-gray-400"
                    }`}
                  />
                  <motion.input
                    variants={inputVariants}
                    custom={1}
                    initial="hidden"
                    animate="visible"
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    autoComplete="current-password"
                    value={Login.password}
                    onChange={handleChange}
                    onFocus={() => setFocused("password")}
                    onBlur={() => setFocused(null)}
                    placeholder="••••••••"
                    className="w-full py-3 pl-10 text-sm text-gray-900 bg-transparent outline-none pr-11 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-600"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute p-1 text-gray-400 transition-colors rounded-md right-3 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <AnimatePresence mode="wait" initial={false}>
                      <motion.span
                        key={showPassword ? "off" : "on"}
                        initial={{ opacity: 0, rotate: -15 }}
                        animate={{ opacity: 1, rotate: 0 }}
                        exit={{ opacity: 0, rotate: 15 }}
                        transition={{ duration: 0.15 }}
                        className="block"
                      >
                        {showPassword ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </motion.span>
                    </AnimatePresence>
                  </button>
                </div>
              </motion.div>

              {/* Signin button */}
              <motion.div
                custom={3}
                variants={fadeUp}
                initial="hidden"
                animate="visible"
                className="pt-1"
              >
                <motion.button
                  type="submit"
                  disabled={loading}
                  whileTap={{ scale: loading ? 1 : 0.98 }}
                  whileHover={{ scale: loading ? 1 : 1.01 }}
                  transition={{ type: "spring", stiffness: 400, damping: 25 }}
                  className={`relative w-full flex items-center justify-center gap-2 px-6 py-2.5 rounded text-sm font-semibold transition-all duration-200 ${
                    loading
                      ? "bg-green-800 dark:bg-green-600 dark:text-white text-gray-600 cursor-not-allowed"
                      : "bg-gradient-to-r from-green-700 to-emerald-800 hover:from-green-600 hover:to-emerald-700 text-white shadow-lg shadow-green-500/25 hover:shadow-green-500/40"
                  }`}
                >
                  <AnimatePresence mode="wait" initial={false}>
                    {loading ? (
                      <motion.span
                        key="loading"
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className="flex items-center gap-2 text-white"
                      >
                        <Loader className="w-4 h-4 animate-spin [animation-duration:0.6s]" />
                        <span>Signing in…</span>
                      </motion.span>
                    ) : (
                      <motion.span
                        key="idle"
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 4 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className="flex items-center gap-2"
                      >
                        <span>Sign in</span>

                        <motion.span
                          animate={{ x: 0 }}
                          whileHover={{ x: 3 }}
                          transition={{
                            type: "spring",
                            stiffness: 400,
                            damping: 20,
                          }}
                        >
                          <ArrowRight className="w-4 h-4" />
                        </motion.span>
                      </motion.span>
                    )}
                  </AnimatePresence>

                  {/* Shimmer on hover */}
                  {!loading && (
                    <span className="absolute inset-0 overflow-hidden pointer-events-none rounded-xl">
                      <motion.span
                        initial={{ x: "-100%" }}
                        whileHover={{ x: "100%" }}
                        transition={{ duration: 0.6, ease: "easeInOut" }}
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent"
                      />
                    </span>
                  )}
                </motion.button>
              </motion.div>
            </form>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
